import React, { useEffect, useMemo, useState } from "react";
import { Button, Spinner } from "reactstrap";
import { useForm } from "react-hook-form";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import FullPageLoader from "Components/Common/FullPageLoader";
import SectionCard from "Components/Common/SectionCard";
import CommonForm from "Components/Common/CommonForm";
import LineClearanceTable from "./LineClearanceTable";
import Reason, { ReasonSubmitPayload } from "common/utilities/Reason/Reason";
import { useGlobalStudy } from "context/StudyContext";
import { GetUserData, showToastMessage } from "common/service/CommonService";
import { useCheckListMasterService } from "modules/Library/Checklist/api/useChecklist";
import { useDispensingLog } from "../../Dispensing/api/useDispensingLog";
import { useLineClearanceService } from "../api/useLineClearance";
import { queryClient } from "api/queryClient";
import {
  LineClearanceFormValues,
  LineClearanceRow,
  Step1DataPackage,
} from "../helpers/lineClearance.types";
import {
  getHeaderFields,
  getIpCategoryFields,
  pharmacistDeclarationFields,
} from "../helpers/lineClearance.constant";
import CommonButton from "Components/Common/CommonButton";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";

interface AddEditLineClearancePharmacistProps {
  editId?: number | string | null;
  isStep1Saved: boolean;
  onSaveSuccess: (dataPackage: Step1DataPackage) => void;
  onNext: () => void;
}

const AddEditLineClearancePharmacist: React.FC<AddEditLineClearancePharmacistProps> = ({
  editId,
  isStep1Saved,
  onSaveSuccess,
  onNext,
}) => {
  /* -----------------------------------------------------------------
   * 1. State & Hooks
   * ----------------------------------------------------------------- */
  const userData = GetUserData();
  const { selectedStudy: globalStudy } = useGlobalStudy();
  const [checklist, setChecklist] = useState<LineClearanceRow[]>([]);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [pendingFormData, setPendingFormData] = useState<LineClearanceFormValues | null>(null);

  /* -----------------------------------------------------------------
   * 2. API Queries 
   * ----------------------------------------------------------------- */
  const { InsertMutation, UpdateMutation, SelectQuery, DropdownQuery: studySelectAllQuery } = useLineClearanceService({
    lineclearanceid: editId ? Number(editId) : null,
    getDropdown: true,
  });

  const { SelectAllQuery: checklistMasterQuery } = useCheckListMasterService({
    ispagination: false,
    checklisttype: "Line Clearance",
  });


  /* -----------------------------------------------------------------
   * 3. React Hook Form Setup
   * ----------------------------------------------------------------- */
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<LineClearanceFormValues>({
    defaultValues: {
      reasonforclearance: "",
      studycode: (globalStudy as any)?.studycode || globalStudy?.studyname || "",
      ipcategory: "",
      productname: "",
      clearancedate: new Date().toISOString().split("T")[0],
      decisionofa: null,
      decisionofb: null,
    },
  });

  // Watch form field values
  const selectedStudyCode = watch("studycode");
  const selectedIpCategory = watch("ipcategory");
  const selectedPeriod = watch("period");

  /* -----------------------------------------------------------------
   * 4. Derived Data & Dropdown Options (useMemo)
   * ----------------------------------------------------------------- */

  // Selected record from SelectQuery
  const selectedRecord = useMemo(() => {
    const rawData = SelectQuery.data?.data;
    if (Array.isArray(rawData)) return rawData[0];
    return rawData || null;
  }, [SelectQuery.data]);
  
  // Master Study Dropdown options
  const studyOptions = useMemo(
    () =>
      studySelectAllQuery.data?.data?.map((study: any) => ({
        label: study.studycode,
        value: study.studycode,
        studyid: study.studyid,
        periods: study.periods,
        inventory: study.inventory,
      })) || [],
    [studySelectAllQuery.data]
  );

  // Currently selected Study object
  const selectedStudy = useMemo(
    () => studyOptions.find((s: any) => s.value === selectedStudyCode),
    [selectedStudyCode, studyOptions]
  );

  // IP Category options dependent on selected study
  const ipCategoryOptions = useMemo(() => {
    const codes = selectedStudy?.inventory || [];
    return codes.map((code: any) => ({
      label: code.ipcategory,
      value: code.ipcategory,
    }));
  }, [selectedStudy]);

  // Period options dependent on selected study
  const periodOptions = useMemo(() => {
    const periods = selectedStudy?.periods || [];
    return periods.map((period: { periodid: number; periodnumber: string }) => ({
      label: period.periodnumber,
      value: period.periodid,
    }));
  }, [selectedStudy]);

  /* -----------------------------------------------------------------
   * 5. (useEffect)
   * ----------------------------------------------------------------- */

  // Populate form fields in Edit Mode from selectedRecord
  useEffect(() => {
    if (!selectedRecord) return;

    if (selectedRecord.studycode) setValue("studycode", selectedRecord.studycode);
    if (selectedRecord.ipcategory) setValue("ipcategory", selectedRecord.ipcategory);
    if (selectedRecord.productname) setValue("productname", selectedRecord.productname);
    if (selectedRecord.periodid) setValue("period", selectedRecord.periodid);
    if (selectedRecord.reasonforclearance) setValue("reasonforclearance", selectedRecord.reasonforclearance);
    if (selectedRecord.clearancedate) {
      setValue("clearancedate", (selectedRecord.clearancedate).split("T")[0]);
    }
    if (selectedRecord.clearancetime) setValue("clearancetime", selectedRecord.clearancetime);
    if (selectedRecord.performerdecision !== undefined && selectedRecord.performdecision !== null) {
      setValue("decisionofa", selectedRecord.performerdecision ? "yes" : "no");
    }

    if (selectedRecord.checklist && Array.isArray(selectedRecord.checklist)) {
      const items: LineClearanceRow[] = selectedRecord.checklist.map((q: any, idx: number) => ({
        id: q.lineclearancechecklistid || idx + 1,
        particulars: q.checklistquestion || "",
        pharmacistInfo: q.performedanswer || "yes",
        qaInfo: q.reviewedanswer || "yes",
        qaRemark: q.remark || "",
      }));
      setChecklist(items);
    }
  }, [selectedRecord, setValue]);

  // Sync Global Context Study with Form (Add Mode)
  useEffect(() => {
    if (selectedRecord) return;
    if (globalStudy?.studyid && studyOptions.length > 0) {
      const matchedStudy = studyOptions.find(
        (s: any) => String(s.studyid) === String(globalStudy.studyid)
      );
      if (matchedStudy) {
        setValue("studycode", matchedStudy.value, { shouldValidate: true, shouldDirty: true });
      }
    } else {
      setValue("studycode", "", { shouldValidate: true, shouldDirty: true });
    }
  }, [globalStudy?.studyid, studyOptions, setValue, selectedRecord]);

  // Auto-fill product name when IP category changes
  useEffect(() => {
    if (!selectedIpCategory || !selectedStudy?.inventory) {
      if (!selectedRecord?.productname) {
        setValue("productname", "");
      }
      return;
    }

    const selectedInventory = selectedStudy.inventory.find(
      (item: any) => item.ipcategory === selectedIpCategory
    );
    setValue("productname", selectedInventory?.productname || "", {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [selectedIpCategory, selectedStudy, setValue, selectedRecord]);

  // Pre-fill default checklist rows for Add Mode only
  useEffect(() => {
    if (selectedRecord || editId) return;
    const rawQuestions: any[] = checklistMasterQuery?.data?.data ?? [];

    if (rawQuestions && rawQuestions.length > 0) {
      const items: LineClearanceRow[] = rawQuestions.map((q: any, idx: number) => ({
        id: q.checklistid || idx + 1,
        particulars: q.question || q.checklistquestion || "",
        pharmacistInfo: "yes",
        qaInfo: "yes",
        qaRemark: "",
      }));
      setChecklist(items);
    }
  }, [checklistMasterQuery?.data?.data, selectedRecord, editId]);

  /* -----------------------------------------------------------------
   * 6. Event Handlers & Form Submission
   * ----------------------------------------------------------------- */
  const handlePharmacistChange = (index: number, value: "yes" | "no" | "na") => {
    setChecklist((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, pharmacistInfo: value } : item))
    );
  };

  const onSavePharmacistStep = (formData: LineClearanceFormValues) => {
    if (editId) {
      setPendingFormData(formData);
      setIsUpdateModalOpen(true);
    } else {
      const selectedInventory = selectedStudy?.inventory?.find(
        (item: any) => item.ipcategory === formData.ipcategory
      );

      const selectedPeriodObj = selectedStudy?.periods?.find(
        (p: any) => String(p.periodid) === String(formData.period)
      );

      const formattedChecklist = checklist.map((item) => ({
        checklistquestion: item.particulars || "",
        performedanswer: item.pharmacistInfo || "yes",
      }));

      const payload = {
        studyid: selectedStudy?.studyid ? Number(selectedStudy.studyid) : null,
        ipid: selectedInventory?.ipid ? Number(selectedInventory.ipid) : null,
        periodid: formData.period ? Number(formData.period) : null,
        periodnumber: selectedPeriodObj?.periodnumber || "",
        reasonforclearance: formData.reasonforclearance || "",
        clearancedate: formData.clearancedate || new Date().toISOString().split("T")[0],
        clearancetime: formData.clearancetime,
        performerdecision: formData.decisionofa === "yes" ? true : false,
        checklist: formattedChecklist,
      };

      InsertMutation.mutate(payload, {
        onSuccess: (res: any) => {
          queryClient.invalidateQueries({ queryKey: ["line-clearance-select-all"] });
          queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
          showToastMessage("success", res?.message || "Pharmacist Line Clearance details saved successfully.");
          onSaveSuccess({
            createdData: res?.data?.[0] || res?.data,
          });
        },
        onError: (error: any) => {
          showToastMessage("error", error || "Failed to save Line Clearance");
        },
      });
    }
  };

  const handleUpdate = (reasonPayload: ReasonSubmitPayload) => {
    const formData = pendingFormData;
    if (!formData) return;

    const selectedInventory = selectedStudy?.inventory?.find(
      (item: any) => item.ipcategory === formData.ipcategory
    );

    const selectedPeriodObj = selectedStudy?.periods?.find(
      (p: any) => String(p.periodid) === String(formData.period)
    );

    const updatedChecklist = editId
      ? checklist.filter((item) => {
          const originalMatch = selectedRecord?.checklist?.find(
            (orig: any, idx: number) =>
              (orig.lineclearancechecklistid &&
                item.id &&
                String(orig.lineclearancechecklistid) === String(item.id)) ||
              idx + 1 === item.id ||
              orig.checklistquestion === item.particulars
          );
          if (!originalMatch) return true;
          return item.pharmacistInfo !== (originalMatch.performedanswer || "yes");
        })
      : checklist;

    const formattedChecklist = updatedChecklist.map((item) => ({
      lineclearancechecklistid: item.id,
      checklistquestion: item.particulars || "",
      performedanswer: item.pharmacistInfo || "yes",
    }));

    const payload = {
      lineclearanceid: Number(editId),
      studyid: selectedStudy?.studyid ? Number(selectedStudy.studyid) : null,
      ipid: selectedInventory?.ipid ? Number(selectedInventory.ipid) : null,
      periodid: formData.period ? Number(formData.period) : null,
      periodnumber: selectedPeriodObj?.periodnumber || "",
      reasonforclearance: formData.reasonforclearance || "",
      clearancedate: formData.clearancedate || new Date().toISOString().split("T")[0],
      clearancetime: formData.clearancetime,
      performedby: Number(userData?.userid),
      updatedby: Number(userData?.userid),
      performerdecision: formData.decisionofa === "yes" ? true : false,
      checklist: formattedChecklist,
      updatereason: reasonPayload.reason,
    };

    UpdateMutation.mutate(payload, {
      onSuccess: (res: any) => {
        queryClient.invalidateQueries({ queryKey: ["line-clearance-select-all"] });
        queryClient.invalidateQueries({ queryKey: ["line-clearance-select"] });
        queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
        showToastMessage("success", res?.message || "Line Clearance details updated successfully.");
        setIsUpdateModalOpen(false);
        onSaveSuccess({
          createdData: res?.data?.[0] || res?.data,
        });
      },
      onError: (error: any) => {
        showToastMessage("error", error || "Failed to update Line Clearance");
      },
    });
  };

  /* -----------------------------------------------------------------
   * 7. Form Render
   * ----------------------------------------------------------------- */
  if (editId && (SelectQuery.isLoading || SelectQuery.isFetching)) {
    return <FullPageLoader message="Loading Line Clearance details..." />;
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSavePharmacistStep)}>
        {/* Section 1: Line Clearance Details */}
        <SectionCard
          index={1}
          icon="ri-file-text-line"
          title="Line Clearance Details"
          subtitle="Specify study protocol, reason, IMP details, and clearance date."
        >
          <CommonForm
            fields={getHeaderFields(studyOptions, periodOptions)}
            control={control}
            errors={errors}
            setValue={setValue}
            columns={3}
            labelShow={true}
          />

          {/* IP Category radio group */}
          <div className="mt-3">
            <CommonForm
              fields={getIpCategoryFields(selectedStudyCode || "", ipCategoryOptions)}
              control={control}
              errors={errors}
              setValue={setValue}
              columns={1}
              labelShow={true}
            />
          </div>
        </SectionCard>

        {/* Section 2: Line Clearance Checklist Table (Pharmacist only) */}
        <SectionCard
          index={2}
          icon="ri-checkbox-multiple-line"
          title="Line Clearance Checklist"
          subtitle="Pharmacist (A) verification checklist across all parameters."
        >
          <LineClearanceTable
            checklist={checklist}
            onPharmacistChange={handlePharmacistChange}
            hideQaColumns={true}
          />
        </SectionCard>

        {/* Section 3: Declaration by Pharmacist */}
        <SectionCard
          index={3}
          icon="ri-shield-check-line"
          title="Line Clearance Declaration (Pharmacist)"
          subtitle="Record clearance status decision for Pharmacist / Designee."
        >
          <CommonForm
            fields={pharmacistDeclarationFields}
            control={control}
            errors={errors}
            setValue={setValue}
            columns={1}
            labelShow={true}
          />
        </SectionCard>

        {/* Sticky Action Footer */}
        <div className={formStyles.stickyFooter}>
          <div className="d-flex justify-content-end gap-2 w-100">
            <CommonButton
              module={PERMISSION_MODULES.LINE_CLEARANCE}
              action={PERMISSION_ACTIONS.LINE_CLEARANCE_PERFORM}
              type="submit"
              color="primary"
              outline
              disabled={editId ? UpdateMutation.isPending : (isStep1Saved || InsertMutation.isPending)}
            >
              {(InsertMutation.isPending) && (
                <Spinner size="sm" className="me-2" />
              )}
              {editId ? "Update" : "Save"}
            </CommonButton>
            <Button
              type="button"
              color="primary"
              outline
              disabled={!isStep1Saved}
              onClick={onNext}
            >
              Next
            </Button>
          </div>
        </div>
      </form>

      {/* Reason Modal on Update */}
      {isUpdateModalOpen && (
        <Reason
          isOpen={isUpdateModalOpen}
          toggle={() => setIsUpdateModalOpen(false)}
          click={handleUpdate}
          action="Update Line Clearance Record"
          id={editId ? Number(editId) : null}
          isLoading={UpdateMutation.isPending}
        />
      )}
    </>
  );
};

export default AddEditLineClearancePharmacist;
